import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { get, post } from '../lib/api';
import { Avatar, ErrorNote, Spinner } from '../components/ui';
import { STARTER_GROUPS, STARTER_TURN_JOBS, frequencyWord, type StarterChore } from '../lib/starterChores';
import type { Child, TurnJob } from '../lib/types';

/** Titles are compared loosely, so a second run skips what is already there. */
function titleKey(chore: StarterChore): string {
  return chore.title.trim().toLowerCase();
}

/** "everyone", or the id of the one child who does it. */
type Assignment = 'all' | 'none' | number;

export default function StarterChores(): JSX.Element {
  const navigate = useNavigate();
  const [children, setChildren] = useState<Child[] | null>(null);
  const [assignment, setAssignment] = useState<Record<string, Assignment>>({});
  const [existing, setExisting] = useState<Record<number, string[]>>({});
  const [jobTitles, setJobTitles] = useState<string[]>([]);
  const [wantJobs, setWantJobs] = useState(true);
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    get<{ children: Child[] }>('/api/users')
      .then(async (res) => {
        const active = res.children.filter((child) => child.active === 1);
        setChildren(active);
        // Start each section where the poster puts it: on the named child when
        // the household has one, otherwise on everybody.
        const start: Record<string, Assignment> = {};
        for (const group of STARTER_GROUPS) {
          const named = group.owner
            ? active.find((child) => child.name.toLowerCase() === group.owner!.toLowerCase())
            : undefined;
          start[group.name] = named ? named.id : 'all';
        }
        setAssignment(start);
        // Titles a child already has, so a second run doesn't duplicate them.
        // Retired chores count too: "remove" keeps the row so history survives,
        // and re-adding the title would leave two of them, one of them dead.
        const seen: Record<number, string[]> = {};
        await Promise.all(
          active.map(async (child) => {
            const list = await get<{ chores: { title: string }[] }>(
              `/api/chores?childId=${child.id}&includeInactive=1`,
            );
            seen[child.id] = list.chores.map((chore) => chore.title.trim().toLowerCase());
          }),
        );
        setExisting(seen);
        const jobs = await get<{ jobs: TurnJob[] }>('/api/turn-jobs');
        setJobTitles(jobs.jobs.map((job) => job.title.trim().toLowerCase()));
      })
      .catch((err) => setError((err as Error).message));
  }, []);

  const plan = useMemo(() => {
    const jobs: { child: Child; chore: StarterChore }[] = [];
    if (!children) return jobs;
    for (const group of STARTER_GROUPS) {
      const who = assignment[group.name];
      if (who === 'none') continue;
      const targets = who === 'all' ? children : children.filter((child) => child.id === who);
      for (const child of targets) {
        for (const chore of group.chores) {
          if ((existing[child.id] ?? []).includes(titleKey(chore))) continue;
          jobs.push({ child, chore });
        }
      }
    }
    return jobs;
  }, [children, assignment, existing]);

  const jobPlan =
    wantJobs && children && children.length > 1
      ? STARTER_TURN_JOBS.filter((job) => !jobTitles.includes(job.title.trim().toLowerCase()))
      : [];

  async function addAll() {
    setBusy(true);
    setError(null);
    try {
      for (const job of jobPlan) {
        await post('/api/turn-jobs', {
          title: job.title,
          icon: job.icon,
          notes: job.notes,
          points: job.points,
          childIds: (children ?? []).map((child) => child.id),
        });
      }
      setJobTitles((current) => [...current, ...jobPlan.map((job) => job.title.trim().toLowerCase())]);
      for (const { child, chore } of plan) {
        await post('/api/chores', {
          child_id: child.id,
          title: chore.title,
          icon: chore.icon,
          notes: chore.notes,
          frequency: chore.frequency,
          points: chore.points,
          weekdays: chore.weekdays,
          due_weekday: chore.dueWeekday,
        });
      }
      setAdded(plan.length + jobPlan.length);
      const seen = { ...existing };
      for (const { child, chore } of plan) {
        seen[child.id] = [...(seen[child.id] ?? []), titleKey(chore)];
      }
      setExisting(seen);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!children) return <Spinner />;

  return (
    <>
      <div className="appbar">
        <Link className="btn ghost" to="/family">
          ‹ Manage family
        </Link>
      </div>
      <h1>Our chore list</h1>
      <p className="small muted" style={{ marginTop: -4 }}>
        The Power Chore List from the fridge. Choose who does each section, then add them all at once.
        Everything stays editable afterwards, and chores a child already has are skipped.
      </p>
      <ErrorNote error={error} />

      {STARTER_GROUPS.map((group) => {
        const who = assignment[group.name] ?? 'all';
        return (
          <div className="card" key={group.name}>
            <h3 style={{ marginBottom: 6 }}>{group.name}</h3>
            <div className="chips" style={{ marginBottom: 10 }}>
              <Chip on={who === 'all'} onClick={() => setAssignment((a) => ({ ...a, [group.name]: 'all' }))}>
                Both
              </Chip>
              {children.map((child) => (
                <Chip
                  key={child.id}
                  on={who === child.id}
                  onClick={() => setAssignment((a) => ({ ...a, [group.name]: child.id }))}
                >
                  <Avatar emoji={child.emoji} /> {child.name}
                </Chip>
              ))}
              <Chip on={who === 'none'} onClick={() => setAssignment((a) => ({ ...a, [group.name]: 'none' }))}>
                Skip
              </Chip>
            </div>
            {group.chores.map((chore) => {
              const done =
                who !== 'none' &&
                (who === 'all'
                  ? children.every((child) => (existing[child.id] ?? []).includes(titleKey(chore)))
                  : (existing[who] ?? []).includes(titleKey(chore)));
              return (
                <div className="row between" key={chore.title} style={{ opacity: who === 'none' ? 0.45 : 1 }}>
                  <span className="row grow">
                    <span style={{ fontSize: 20 }}>{chore.icon}</span>
                    <span className="grow">
                      <span>{chore.title}</span>
                      <span className="tiny muted" style={{ display: 'block' }}>
                        {frequencyWord(chore)} · {chore.points} {chore.points === 1 ? 'point' : 'points'}
                        {done ? ' · already added' : ''}
                      </span>
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        );
      })}

      {children.length > 1 && (
        <div className="card">
          <h3 style={{ marginBottom: 6 }}>Shared jobs</h3>
          <p className="tiny muted" style={{ marginTop: 0 }}>
            Taken in turns rather than due on a day. Whoever has done fewest is up next, and doing an
            extra one puts them ahead — the same list they kept on paper.
          </p>
          <div className="chips" style={{ marginBottom: 10 }}>
            <Chip on={wantJobs} onClick={() => setWantJobs(true)}>
              Both, in turns
            </Chip>
            <Chip on={!wantJobs} onClick={() => setWantJobs(false)}>
              Skip
            </Chip>
          </div>
          {STARTER_TURN_JOBS.map((job) => (
            <div className="row between" key={job.title} style={{ opacity: wantJobs ? 1 : 0.45 }}>
              <span className="row grow">
                <span style={{ fontSize: 20 }}>{job.icon}</span>
                <span className="grow">
                  <span>{job.title}</span>
                  <span className="tiny muted" style={{ display: 'block' }}>
                    In turns · {job.points} {job.points === 1 ? 'point' : 'points'} each
                    {jobTitles.includes(job.title.trim().toLowerCase()) ? ' · already added' : ''}
                  </span>
                </span>
              </span>
            </div>
          ))}
        </div>
      )}

      <button
        className="btn block"
        type="button"
        disabled={busy || plan.length + jobPlan.length === 0}
        onClick={() => void addAll()}
      >
        {busy
          ? 'Adding…'
          : plan.length + jobPlan.length === 0
            ? added === null
              ? 'Nothing left to add'
              : `Added ${added} chores ✓`
            : `Add ${plan.length + jobPlan.length} ${plan.length + jobPlan.length === 1 ? 'chore' : 'chores'}`}
      </button>
      {added !== null && plan.length + jobPlan.length === 0 && (
        <button className="btn ghost block" type="button" style={{ marginTop: 8 }} onClick={() => navigate('/')}>
          Back to the dashboard
        </button>
      )}
    </>
  );
}

function Chip({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button type="button" className={`chip who-chip${on ? ' on' : ''}`} onClick={onClick}>
      {children}
    </button>
  );
}
